import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import ChartWrapper from "@qognicafinance/react-lightweight-charts";
import { fetchCoinDetails, fetchCoinOHLC } from "../utils/coinGeckoAPI";

export default function CoinDetails() {
  const { id } = useParams();
  const [coin, setCoin] = useState(null);
  const [candles, setCandles] = useState([]);
  const [days, setDays] = useState(1);
  const [chartLoading, setChartLoading] = useState(false);
  const [coinLoading, setCoinLoading] = useState(true);
  const [chartError, setChartError] = useState(null);
  const [coinError, setCoinError] = useState(null);

  useEffect(() => {
    async function fetchCoinInfo() {
      setCoinLoading(true);
      setCoinError(null);
      try {
        const data = await fetchCoinDetails(id);
        setCoin(data);
      } catch (err) {
        console.error('Error fetching coin details:', err);
        setCoinError('Failed to load coin details. Please try again later.');
      } finally {
        setCoinLoading(false);
      }
    }

    async function fetchOHLC() {
      // Only allow days 1, 7, 30
      const allowedDays = [1, 7, 30];
      const safeDays = allowedDays.includes(days) ? days : 1;
      
      setChartLoading(true);
      setChartError(null);
      
      try {
        const ohlcData = await fetchCoinOHLC(id, safeDays);
        setCandles(ohlcData);
      } catch (err) {
        console.error('Error fetching OHLC data:', err);
        setChartError('Failed to load chart data. Please try again later.');
      } finally {
        setChartLoading(false);
      }
    }

    fetchCoinInfo();
    fetchOHLC();
  }, [id, days]);



  if (coinLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-gray-400">Loading coin data...</div>
      </div>
    );
  }

  if (coinError) {
    return (
      <div className="p-4 bg-red-900/20 border border-red-900/30 rounded-lg text-red-200 text-center">
        {coinError}
      </div>
    );
  }

  if (!coin) {
    return <div className="text-gray-300">No data available for this coin.</div>;
  }

  return (
    <div>
      <div className="flex items-center gap-4 mb-4">
        <img src={coin.image.large} alt={coin.name} className="w-10 h-10" />
        <h1 className="text-2xl font-bold">{coin.name} ({coin.symbol.toUpperCase()})</h1>
      </div>

      <div className="mb-6">
        <p>💰 Current Price: <strong>${coin.market_data.current_price.usd.toLocaleString()}</strong></p>
        <p>📈 24h Change:
          <span className={`ml-2 ${coin.market_data.price_change_percentage_24h >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {coin.market_data.price_change_percentage_24h?.toFixed(2)}%
          </span>
        </p>
      </div>

      <div className="mb-4 space-x-2">
        {[1, 7, 30].map((d) => (
          <button
            key={d}
            onClick={() => setDays(d)}
            className={`px-4 py-2 rounded-md ${
              days === d ? "bg-primary text-white" : "bg-gray-700 text-gray-300"
            }`}
          >
            {d === 1 ? "1D" : d === 7 ? "7D" : "30D"}
          </button>
        ))}
      </div>

      <div className="mb-6 rounded-lg shadow-xl border border-gray-700 bg-background min-h-[420px] flex items-center justify-center">
        {chartLoading ? (
          <div className="text-gray-400 text-center">Loading chart...</div>
        ) : chartError ? (
          <div className="text-red-400 text-center">{chartError}</div>
        ) : (
          <ChartWrapper
            options={{
              layout: {
                background: { color: "#0f172a" },
                textColor: "#e2e8f0",
              },
              grid: {
                vertLines: { color: "#334155" },
                horzLines: { color: "#334155" },
              },
              priceScale: { borderColor: "#475569" },
              timeScale: { borderColor: "#475569", timeVisible: true, secondsVisible: false },
            }}
            candlestickSeries={[{
              data: candles,
              options: {
                upColor: "#22c55e",
                borderUpColor: "#22c55e",
                wickUpColor: "#22c55e",
                downColor: "#ef4444",
                borderDownColor: "#ef4444",
                wickDownColor: "#ef4444",
              }
            }]}
            autoWidth
            height={400}
          />
        )}
      </div>

      <p className="text-gray-400 text-sm">
        Powered by CoinGecko • Candlestick data ({days === 1 ? '1-day' : days === 7 ? '7-day' : '30-day'} interval)
      </p>
    </div>
  );
}
